import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { storefrontClient } from '../apollo/shopify/storefrontClient';
import {
  AvailableDraw,
  DrawGateway,
  DrawWithFrames,
} from '../features/draws/types';
import { ShopifyProductsGateway } from '../gateways/ShopifyProductsGateway';
import { ApplicationState } from '../store/types';
import { useAuth } from './AuthProvider';

const shopifyProductsGateway = new ShopifyProductsGateway(storefrontClient);

interface DrawContextProps {
  children: ReactNode;
  mainDraw?: AvailableDraw;
  linkedDraws: AvailableDraw[];
  drawGateway: DrawGateway;
}

type DrawContextType = {
  mainDraw?: DrawWithFrames;
  linkedDraws: DrawWithFrames[];
  isAccountInDraw: (drawId: string) => boolean;
  // selectedDraws is used to store the draws that the user has selected to enter. This is used for draws that are linked so the user selects draws before going on to enter them
  selectedDraws: string[];
  selectDraw: (drawId: string) => void;
  winningDrawsCount: number;
  setWinningDrawsCount: (count: number) => void;
  drawIdsEntered: string[];
};

const DrawContext = createContext<DrawContextType>({
  mainDraw: undefined,
  linkedDraws: [] as DrawWithFrames[],
  isAccountInDraw: () => false,
  selectedDraws: [],
  selectDraw: () => {},
  winningDrawsCount: 0,
  setWinningDrawsCount: () => {},
  drawIdsEntered: [],
});

export const DrawProvider = (props: DrawContextProps) => {
  const { children, drawGateway, linkedDraws, mainDraw } = props;
  const { user } = useAuth();
  const [mainDrawEnriched, setMainDrawEnriched] = useState<
    DrawWithFrames | undefined
  >(mainDraw);
  const [linkedDrawsEnriched, setLinkedDrawsEnriched] =
    useState<DrawWithFrames[]>(linkedDraws);
  const [selectedDraws, setSelectedDraws] = useState<string[]>(
    mainDraw?.draw.id ? [mainDraw.draw.id] : [],
  );
  const [winningDrawsCount, setWinningDrawsCount] = useState(1);
  // redux for when user has just entered the draw and a refresh has not been done
  const drawEntries = useSelector(
    (s: ApplicationState) => s.features.draw.drawEntry,
  );

  const allDraws = useMemo(
    () => [...linkedDraws, mainDraw],
    [linkedDraws, mainDraw],
  );

  const [drawEntryStatus, setDrawEntryStatus] = useState<
    Record<string, boolean>
  >(
    allDraws.reduce(
      (acc, draw) => {
        acc[draw?.draw.id ?? ''] = false;
        return acc;
      },
      {} as Record<string, boolean>,
    ),
  );

  const enrichDraw = async (draw: DrawWithFrames): Promise<DrawWithFrames> => {
    const data = await shopifyProductsGateway.getProductPurchaseDetails({
      identifier: {
        id: draw.draw.productId,
      },
      releaseName: 'draw',
    });
    return {
      ...draw,
      product: {
        ...draw.product,
        // override default variant price with the price retrived when given the right release name
        price: data?.price ?? draw.product.price,
      },
      frames: data?.frames,
    };
  };

  useEffect(() => {
    if (mainDraw) {
      enrichDraw(mainDraw).then(setMainDrawEnriched);
    }
  }, [mainDraw]);

  useEffect(() => {
    const enrichPromises = linkedDraws.map(enrichDraw);

    Promise.all(enrichPromises).then(setLinkedDrawsEnriched);
  }, [linkedDraws]);

  useEffect(() => {
    if (user) {
      const drawEntryPromises = allDraws
        .filter(Boolean)
        .map(async (draw) =>
          drawGateway.getDrawByProductId(draw!.product.shopifyId),
        );

      Promise.all(drawEntryPromises).then((entries) => {
        setDrawEntryStatus(
          entries.filter(Boolean).reduce(
            (acc, draw) => ({
              ...acc,
              [draw!.id]: draw!.accountIsInDraw,
            }),
            {} as Record<string, boolean>,
          ),
        );
      });
    }
  }, [allDraws, user]);

  const isAccountInDraw = useCallback(
    (drawId: string) =>
      drawEntryStatus[drawId] || Boolean(drawEntries[drawId]?.entered),
    [drawEntryStatus, drawEntries],
  );

  const drawIdsEntered = useMemo(() => {
    const ids = [
      ...(mainDrawEnriched?.draw.id ? [mainDrawEnriched.draw.id] : []),
      ...linkedDrawsEnriched.map((draw) => draw.draw.id),
    ].filter((drawId) => isAccountInDraw(drawId));
    setSelectedDraws((prev) => [...new Set([...prev, ...ids])]);
    return ids;
  }, [mainDrawEnriched, linkedDrawsEnriched, isAccountInDraw]);

  const context = useMemo(
    () => ({
      mainDraw: mainDrawEnriched,
      linkedDraws: linkedDrawsEnriched,
      isAccountInDraw,
      selectedDraws,
      selectDraw: (drawId: string) => {
        setSelectedDraws((prev) => {
          if (prev.includes(drawId)) {
            return prev.filter((id) => id !== drawId);
          }
          return [...prev, drawId];
        });
      },
      drawIdsEntered,
      winningDrawsCount,
      setWinningDrawsCount,
    }),
    [
      mainDrawEnriched,
      linkedDrawsEnriched,
      selectedDraws,
      drawEntryStatus,
      drawEntries,
      winningDrawsCount,
      setWinningDrawsCount,
    ],
  );

  return (
    <DrawContext.Provider value={context}>{children}</DrawContext.Provider>
  );
};

export const useDraws = () => useContext(DrawContext);
